import { Blueprint } from '@/components/builder/base/blueprints/Blueprint';
import { ValidatableBlueprint } from '@/components/builder/base/blueprints/ValidatableBlueprint';
import { HasWidth } from '@/components/builder/base/traits/HasWidth';
import { ValidationErrors } from '@/components/builder/base/types/ValidationErrors';
import { FormBuilderContract } from '@/components/builder/form';
import { BlueprintDefinition } from '@/components/builder/form/blueprints/BlueprintDefinition';
import { ReadonlyBlueprint } from '@/components/builder/form/blueprints/ReadonlyBlueprint';
import { RequiredBlueprint } from '@/components/builder/form/blueprints/RequiredBlueprint';
import { VisibleBlueprint } from '@/components/builder/form/blueprints/VisibleBlueprint';
import { Entry, entry } from '@/components/builder/form/entries/Entry';
import { ValidEntry } from '@/components/builder/form/entries/ValidEntry';
import { AlwaysChoice } from '@/components/builder/form/enums/AlwaysChoice';
import { InternallyChoice } from '@/components/builder/form/enums/InternallyChoice';
import { NeverChoice } from '@/components/builder/form/enums/NeverChoice';
import { WhenChoice } from '@/components/builder/form/enums/WhenChoice';
import { EntryFactory } from '@/components/builder/form/traits/EntryFactory';
import { HasHelp } from '@/components/builder/form/traits/HasHelp';
import { HasLabel } from '@/components/builder/form/traits/HasLabel';
import { ProcessCallback } from '@/components/builder/form/types/ProcessCallback';
import { DateTime } from 'luxon';

export const Definition: BlueprintDefinition = {
    type: "cycle",
    name: "[[[Cykl]]]",
    icon: "far fa-calendar-alt",
    group: "logito",
    position: 8,
};

export enum CycleEndType {
    None = 'None',
    AfterNOcurrences = 'AfterNOcurrences',
    AfterDate = 'AfterDate'
}

export enum CycleFrequencyType {
    Daily = 'Daily',
    Weekly = 'Weekly',
    Monthly = 'Monthly'
}

export enum DailyCycleSettingsType {
    EveryNDays = 'EveryNDays',
    EveryWorkDay = 'EveryWorkDay'
}

export enum MonthlyCycleSettingsType {
    SetDayNumber = 'SetDayNumber',
    SetDayOfWeek = 'SetDayOfWeek'
}

export class CycleEntryData
{
    public endType: CycleEndType = CycleEndType.None;
    public occurences: number | null = null;
    public startDate: DateTime | null = null;
    public startTime: string | null = null;
    public endTime: string | null = null;
    public endDate: DateTime | null = null;
    public frequencyType: CycleFrequencyType = CycleFrequencyType.Daily;
    public dailyCycleSetting: DailyCycleSettings = new DailyCycleSettings();
    public weeklyCycleSetting: WeeklyCycleSettings = new WeeklyCycleSettings();
    public monthlyCycleSetting: MonthlyCycleSettings = new MonthlyCycleSettings();

    public constructor(data: any = null)
    {
        if (data !== null)
        {
            this.endType = data.endType || CycleEndType.None;
            this.occurences = data.occurences || null;
            this.startDate = data.startDate ? DateTime.fromISO(data.startDate) : null;
            this.startTime = data.startTime ? data.startTime : null;
            this.endTime = data.endTime ? data.endTime : null;
            this.endDate = data.endDate ? DateTime.fromISO(data.endDate) : null;
            this.frequencyType = data.frequencyType || CycleFrequencyType.Daily;
            this.dailyCycleSetting = data.dailyCycleSetting ? new DailyCycleSettings(data.dailyCycleSetting) : new DailyCycleSettings();
            this.weeklyCycleSetting = data.weeklyCycleSetting ? new WeeklyCycleSettings(data.weeklyCycleSetting) : new WeeklyCycleSettings();
            this.monthlyCycleSetting = data.monthlyCycleSetting ? new MonthlyCycleSettings(data.monthlyCycleSetting) : new MonthlyCycleSettings();
        }
    }

    public toString(): string
    {
        if (!this.startDate) return '';

        let result = `Od ${this.startDate.toLocaleString()}`;

        if (this.endType === CycleEndType.AfterDate && this.endDate)
        {
            result += ` do ${this.endDate.toLocaleString()}`;
        }
        else if (this.endType === CycleEndType.AfterNOcurrences && this.occurences)
        {
            result += ` (${this.occurences} wystąpień)`;
        }

        result += ` - `;

        const dayNames = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
        const weekNames = ['', 'Pierwszy', 'Drugi', 'Trzeci', 'Czwarty', 'Ostatni'];

        switch (this.frequencyType)
        {
            case CycleFrequencyType.Daily:
                if (this.dailyCycleSetting.type === DailyCycleSettingsType.EveryWorkDay)
                {
                    result += 'Każdy dzień roboczy';
                }
                else
                {
                    result += `Co ${this.dailyCycleSetting.interval} dni`;
                }

                break;
            case CycleFrequencyType.Weekly:
                result += `Co ${this.weeklyCycleSetting.interval} tygodni (${this.weeklyCycleSetting.daysOfWeek.map(d => dayNames[d]).join(', ')})`;
                break;
            case CycleFrequencyType.Monthly:
                if (this.monthlyCycleSetting.type === MonthlyCycleSettingsType.SetDayNumber)
                {
                    result += `Co ${this.monthlyCycleSetting.interval} miesięcy (dzień ${this.monthlyCycleSetting.dayOfMonth})`;
                }
                else
                {
                    result += `Co ${this.monthlyCycleSetting.interval} miesięcy (${weekNames[this.monthlyCycleSetting.weekNumber]} ${dayNames[this.monthlyCycleSetting.dayOfWeek]})`;
                }

                break;
        }

        result += ` o ${this.timeToString(this.startTime)} - ${this.timeToString(this.endTime)}`;

        return result;
    }

    public timeToString(time: string): string
    {
        if (time.match(/^\d{2}:\d{2}$/))
        {
            return `${time}`;
        }
        else if (time.match(/^\d{2}:\d{2}:\d{2}$/))
        {
            const timeParts = time.split(':');

            return `${timeParts[0]}:${timeParts[1]}`;
        }

        return time;
    }
}

export class DailyCycleSettings
{
    public type: DailyCycleSettingsType = DailyCycleSettingsType.EveryNDays;
    public interval: number | null = 1;

    public constructor(data: any = null)
    {
        if (data !== null)
        {
            this.type = data.type || DailyCycleSettingsType.EveryNDays;
            this.interval = data.interval || 1;
        }
    }
}

export class WeeklyCycleSettings
{
    public daysOfWeek: number[] = [];
    public interval: number = 1;

    public constructor(data: any = null)
    {
        if (data !== null)
        {
            this.daysOfWeek = data.daysOfWeek || [];
            this.interval = data.interval || 1;
        }
    }
}

export class MonthlyCycleSettings
{
    public type: MonthlyCycleSettingsType = MonthlyCycleSettingsType.SetDayNumber;
    public dayOfMonth: number | null = 1;
    public dayOfWeek: number | null = null;
    public weekNumber: number | null = null;
    public interval: number = 1;

    public constructor(data: any = null)
    {
        if (data !== null)
        {
            this.type = data.type || MonthlyCycleSettingsType.SetDayNumber;
            this.dayOfMonth = data.dayOfMonth || 1;
            this.dayOfWeek = data.dayOfWeek || null;
            this.weekNumber = data.weekNumber || null;
            this.interval = data.interval || 1;
        }
    }
}

export class CycleEntry extends ValidEntry<CycleEntryData>
{
    public type: string = Definition.type;
    public data: CycleEntryData = null;
    public defaultValue: CycleEntryData = new CycleEntryData();

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = new CycleEntryData(data);
        }
    }

    public async collect(
        blueprint: CycleContract,
        form: FormBuilderContract,
        preprocess: ProcessCallback
    ): Promise<Entry>
    {
        await preprocess(blueprint, this, form.blueprintId, form.entryId);

        const data = this.getValues(blueprint);

        if (data.startTime)
        {
            if (data.startTime.match(/^\d{2}:\d{2}$/))
            {
                data.startTime = `${data.startTime}:00.000000`;
            }
            else if (data.startTime.match(/^\d{2}:\d{2}:\d{2}$/))
            {
                data.startTime = `${data.startTime}.000000`;
            }
            else
                data.startTime = null;
        }

        if (data.endTime)
        {
            if (data.endTime.match(/^\d{2}:\d{2}$/))
            {
                data.endTime = `${data.endTime}:00.000000`;
            }
            else if (data.endTime.match(/^\d{2}:\d{2}:\d{2}$/))
            {
                data.endTime = `${data.endTime}.000000`;
            }
            else
                data.endTime = null;
        }

        return entry(data);
    }

    public validate(blueprint: CycleContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        const value = this.data;

        if (form.expressions.required(blueprint) && (value === null || !value.startDate))
        {
            this.errors.value = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
        }

        return this.valid();
    }

    private getValues(blueprint: CycleContract): CycleEntryData
    {
        if (this.data !== null) return new CycleEntryData(this.data);

        if (blueprint.defaultValue) return new CycleEntryData(blueprint.defaultValue);

        return new CycleEntryData();
    }
}

export const instanceOfCycleEntry = (object: any): object is CycleEntry =>
{
    return object && "type" in object && object.type === Definition.type;
};

export interface CycleContract extends Blueprint, VisibleBlueprint, RequiredBlueprint, ReadonlyBlueprint, HasLabel, HasHelp, HasWidth {
    defaultValue: CycleEntryData;
}

export class CycleType implements CycleContract, ValidatableBlueprint, EntryFactory<CycleEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public help: Record<string, string>;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public errors: ValidationErrors;
    public width: number;
    public minWidth: number;
    public exceptWidth?: number[];
    public defaultValue: CycleEntryData;
    public disabled?: boolean;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.width = 0;
        this.minWidth = 1;
        this.label = { 'pl-PL': 'Cykl' };
        this.showLabel = true;
        this.help = {};
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.defaultValue = new CycleEntryData();
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): CycleEntry
    {
        return new CycleEntry(data);
    }

    public updateEntry(data: any, entry: CycleEntry): CycleEntry
    {
        return new CycleEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors,
        };
    }
}
